import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getBrandConfig } from '../brandConfig';
import { isAdminOrSuperAdmin, getBearerHeaders } from '../lib/auth';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import {
  ChartBarSquareIcon,
  UsersIcon,
  BuildingOffice2Icon,
  MegaphoneIcon,
  LockClosedIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftRightIcon,
  InboxArrowDownIcon,
  DocumentChartBarIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  BeakerIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  UserIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon, // Substitua DocumentArrowDownIcon por ArrowDownTrayIcon
  FlagIcon
} from '@heroicons/react/24/outline';

export default function Sidebar({ aberto }) {
  const location = useLocation();
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const isAdmin = isAdminOrSuperAdmin(userData);
  const userId = userData.id || userData._id || localStorage.getItem('userId');
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net').replace(/\/+$/, '');
  const brand = getBrandConfig();
  const logoSrc = brand.logoUrl || '/brand-logo.png';
  // Estado para armazenar os créditos e o status de carregamento
  const [userCredits, setUserCredits] = useState({ saldo: 0, bloqueado: false });
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [creditError, setCreditError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Função para verificar se o token é válido
  const isTokenValid = () => {
    const token = localStorage.getItem('token');
    return !!token;
  };
  // Função para buscar os créditos da API
  const fetchUserCredits = async () => {
    if (!userId) {
      setCreditError('ID de usuário não encontrado');
      setLoadingCredits(false);
      return;
    }
    try {
      setLoadingCredits(true);
      setCreditError(null);
      const headers = getBearerHeaders();
      if (!headers) {
        throw new Error('Token não encontrado');
      }
      const response = await axios.get(`${baseUrl}/api/usuarios/${encodeURIComponent(String(userId).trim())}/creditos`, {
        headers,
      });
      const creditsData = response.data.creditos || { saldo: 0, bloqueado: false };
      setUserCredits(creditsData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Erro ao buscar créditos:', error);
      const status = error.response?.status;
      // Admin/super_admin: endpoint pode não ter documento (User legado) — não tratar como erro grave
      if (isAdmin && status === 404) {
        setUserCredits({ saldo: 0, bloqueado: false });
        setCreditError(null);
      } else if (error.response) {
        switch (status) {
          case 403:
            setCreditError('Acesso negado - permissão insuficiente');
            break;
          case 404:
            setCreditError('Usuário não encontrado');
            break;
          default:
            setCreditError('Erro ao carregar créditos');
        }
        setUserCredits({ saldo: 0, bloqueado: false });
      } else if (error.request) {
        setCreditError('Sem resposta do servidor');
        setUserCredits({ saldo: 0, bloqueado: false });
      } else {
        setCreditError(error.message || 'Erro desconhecido');
        setUserCredits({ saldo: 0, bloqueado: false });
      }
    } finally {
      setLoadingCredits(false);
    }
  };
  useEffect(() => {
    if (!userId) return;
    if (!isTokenValid()) {
      toast.error("Sessão expirada. Faça login novamente.");
      setLoadingCredits(false);
      return;
    }
    fetchUserCredits();
    const intervalId = setInterval(fetchUserCredits, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [userId]);
  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <ChartBarSquareIcon className="h-6 w-6 shrink-0" /> },
    { path: '/usuarios', label: 'Usuários', icon: <UsersIcon className="h-6 w-6 shrink-0" />, adminOnly: true },
    { path: '/campanhas', label: 'Campanhas', icon: <MegaphoneIcon className="h-6 w-6 shrink-0" /> },
    { path: '/sips', label: 'SIPs', icon: <LockClosedIcon className="h-6 w-6 shrink-0" />, adminOnly: true },
    { path: '/phoneNumber', label: 'Telefones', icon: <DevicePhoneMobileIcon className="h-6 w-6 shrink-0" /> },
    { path: '/assistentes', label: 'Assistentes', icon: <ChatBubbleLeftRightIcon className="h-6 w-6 shrink-0" /> },
    { path: '/tools', label: 'Ferramentas', icon: <BeakerIcon className="h-6 w-6 shrink-0" /> },
    { path: '/structured-outputs', label: 'Estruturas de Saída', icon: <FlagIcon className="h-6 w-6 shrink-0" /> },
    { path: '/relatorios', label: 'Relatórios', icon: <DocumentChartBarIcon className="h-6 w-6 shrink-0" /> },
    { path: '/files', label: 'Arquivos', icon: <ArrowDownTrayIcon className="h-6 w-6 shrink-0" /> }, // Use ArrowDownTrayIcon em vez de DocumentArrowDownIcon
    { path: '/call-logs', label: 'Chamadas', icon: <ClipboardDocumentListIcon className="h-6 w-6 shrink-0" /> },
    { path: '/cotacao', label: 'Câmbio USD/BRL', icon: <CurrencyDollarIcon className="h-6 w-6 shrink-0" />, adminOnly: true },
    { path: '/configuracoes', label: 'Configurações', icon: <Cog6ToothIcon className="h-6 w-6 shrink-0" /> }
  ];
  const visibleMenuItems = menuItems.filter(item => !item.adminOnly || isAdmin);
  const linkClasses =
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground";
  const activeLinkClasses = "bg-accent text-accent-foreground shadow-sm";
  const getCreditStatus = () => {
    if (loadingCredits) {
      return (
        <div className="flex items-center space-x-2">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-purple-400" />
          <span className="text-muted-foreground text-sm font-medium">
            Carregando créditos...
          </span>
        </div>
      );
    }
    if (creditError) {
      return (
        <div className="flex items-center space-x-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
          <span className="text-yellow-500 text-sm font-medium">
            {creditError}
          </span>
          <Button
            onClick={fetchUserCredits}
            title="Tentar novamente"
            variant="ghost"
            size="icon"
            className="ml-2 h-7 w-7"
          >
            <ArrowPathIcon className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      );
    }
    const saldo = parseFloat(userCredits.saldo || 0).toFixed(2);
    let colorClass = 'text-green-400';
    let iconColorClass = 'text-green-400';
    if (userCredits.bloqueado) {
      return (
        <div className="flex items-center space-x-2">
          <CurrencyDollarIcon className="h-5 w-5 text-red-500" />
          <span className="text-red-500 text-sm font-medium flex items-center">
            <LockClosedIcon className="h-4 w-4 mr-1" />
            Conta bloqueada
          </span>
          {lastUpdated && (
            <Button
              onClick={fetchUserCredits}
              title="Atualizar créditos"
              variant="ghost"
              size="icon"
              className="ml-2 h-7 w-7"
            >
              <ArrowPathIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      );
    } else if (parseFloat(saldo) < 0) {
      colorClass = 'text-red-500';
      iconColorClass = 'text-red-500';
    } else if (parseFloat(saldo) === 0) {
      colorClass = 'text-yellow-500';
      iconColorClass = 'text-yellow-500';
    }
    return (
      <div className="flex items-center space-x-2">
        <CurrencyDollarIcon className={`h-5 w-5 ${iconColorClass}`} />
        <span className={`${colorClass} text-sm font-medium`}>
          Créditos: $ {saldo}
        </span>
        {lastUpdated && (
          <Button
            onClick={fetchUserCredits}
            title="Atualizar créditos"
            variant="ghost"
            size="icon"
            className="ml-2 h-7 w-7"
          >
            <ArrowPathIcon className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>
    );
  };
  return (
    <div
      className={cn(
        "flex h-screen flex-col border-r bg-card text-card-foreground shadow-sm transition-all duration-300 ease-in-out",
        aberto ? "w-64" : "w-20"
      )}
    >
      {/* Cabeçalho com logo */}
      <div className="flex items-center justify-center border-b border-border/60 px-3 py-5">
        <img
          src={logoSrc}
          alt={brand.name || 'Logo'}
          className={cn("overflow-hidden transition-all", aberto ? "w-48" : "w-0")}
        />
        <img src={logoSrc} alt={brand.name || 'Logo'} className={cn("w-9", aberto && "hidden")} />
      </div>
      {/* Área de navegação com scroll invisível */}
      <div className="flex-grow overflow-y-auto px-2 py-4 scrollbar-hide">
        <nav>
          <ul className="space-y-2">
            {visibleMenuItems.map(({ path, label, icon }) => (
              <li key={path}>
                <Link to={path} className={`${linkClasses} ${location.pathname === path ? activeLinkClasses : ''}`}>
                  {icon}
                  <span className={`whitespace-nowrap transition-opacity duration-200 ${!aberto && 'opacity-0'}`}>{label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
      {/* Rodapé com informações do usuário e botão de sair */}
      <div className="border-t border-border/60 px-3 pb-4 pt-4">
        <div
          className={cn(
            "mb-3 rounded-lg border border-border/60 bg-muted/60 px-3 py-2 transition-all duration-300",
            !aberto && "flex justify-center"
          )}
        >
          {getCreditStatus()}
        </div>
        {/* Informações do usuário */}
        <div
          className={cn(
            "mb-2 flex items-center gap-3 rounded-md border border-transparent p-2 transition-all duration-300",
            aberto ? "" : "justify-center"
          )}
        >
          {aberto ? (
            <>
              <div className="flex-shrink-0">
                <UserIcon className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="truncate text-sm font-medium">{userData.nome || 'Usuário'}</p>
                <p className="truncate text-xs text-muted-foreground">{userData.email}</p>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant={isAdmin ? "secondary" : "outline"} className="capitalize">
                    {userData.role || 'cliente'}
                  </Badge>
                  {isAdmin && <ShieldCheckIcon className="h-3 w-3 text-yellow-500" />}
                </div>
              </div>
            </>
          ) : (
            <UserIcon className="h-6 w-6 text-primary" />
          )}
        </div>
        {/* Botão de sair */}
        <div className="pb-2">
          <a
            href="/"
            onClick={() => localStorage.clear()}
            className={cn(linkClasses, !aberto && "justify-center")}
          >
            <ArrowLeftOnRectangleIcon className="h-6 w-6 shrink-0" />
            <span className={`whitespace-nowrap transition-opacity duration-200 ${!aberto && 'opacity-0 hidden'}`}>
              Sair
            </span>
          </a>
        </div>
      </div>
    </div>
  );
}
